import { triviaForPacks } from '../content/startupTrivia';

/** Shown as the fixed line in the blank tab and on the card while a Mitti starts. */
export const STARTING_TAB_MESSAGE = 'Turning the mitti… your Mehnat starts in up to 3 minutes';

export const START_TIMEOUT_MESSAGE = 'The Mitti did not start in time. Wait a minute and try again.';

/** Shown in the tab just before it hands over, and on the card for a few minutes after a start. */
export const WARMUP_MESSAGE =
  "Henoyo is warming up. Your first workflow may take a minute or two to run; that's normal.";

export const WARMUP_WINDOW_MS = 5 * 60_000;
const WARMUP_TAB_MS = 3000;

/** True when the Mitti is running and was started within the last 5 minutes (client clock). */
export function warmupNoteVisible(state: string, lastStartedAt: string | null, nowMs: number): boolean {
  if (state !== 'running' || !lastStartedAt) return false;
  const started = Date.parse(lastStartedAt);
  if (!Number.isFinite(started)) return false;
  const age = nowMs - started;
  return age >= 0 && age < WARMUP_WINDOW_MS;
}

const WARMUP_TAB_STYLE =
  'body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;background:#f6f3ee;color:#6b6357;text-align:center;padding:24px;font-size:14px;line-height:1.5}';

const ROTATE_MS = 6000;

/**
 * Hosting cuts the browser off at 60s with a 502 or 504 and no JSON error.
 * The function's own start_timeout is 502 with an error code, and is not "still starting".
 */
export function treatAsStillStarting(status: number, error: string | undefined, elapsedMs: number): boolean {
  if ((status === 502 || status === 504) && !error) return true;
  if (status === 408) return true;
  if (status === 0 && elapsedMs > 15_000) return true;
  return false;
}

/**
 * True when the server recorded a start after this click.
 * lastStartedAt must be strictly later than the value captured before the click.
 */
export function urlIfRestarted(
  previousLastStartedAt: string | null,
  env: { lastStartedAt: string | null; url: string } | null,
): string | null {
  if (!env?.url || !env.lastStartedAt) return null;
  const nextMs = Date.parse(env.lastStartedAt);
  if (!Number.isFinite(nextMs)) return null;
  if (previousLastStartedAt == null) return env.url;
  const prevMs = Date.parse(previousLastStartedAt);
  if (!Number.isFinite(prevMs) || nextMs <= prevMs) return null;
  return env.url;
}

/** https://<host>/ for a stored hostname. Null when host is not a hostname. */
export function httpsOrigin(host: string): string | null {
  const trimmed = host.trim().toLowerCase().replace(/\.$/, '');
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(trimmed)) {
    return null;
  }
  return `https://${trimmed}/`;
}

/**
 * True only when action is exactly https://<stored host>/sso.
 * URL-parsed: https, no userinfo, no port, no query, no hash, path /sso, host equal.
 */
export function ssoActionMatches(action: string, host: string): boolean {
  const origin = httpsOrigin(host);
  if (!origin || !action) return false;
  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  if (url.username || url.password || url.port || url.search || url.hash) return false;
  if (url.pathname !== '/sso') return false;
  return `https://${url.hostname}/` === origin;
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Starting screen for the tab opened on click. Inline colours only: no images,
 * no stylesheets, no scripts from another host. Messages rotate every 6 seconds.
 */
export function writeStartingTab(
  tab: { document: { write: (html: string) => void; close: () => void } } | null,
  packs: readonly string[] = [],
): void {
  if (!tab) return;
  const rotating = triviaForPacks(packs).filter((line) => line !== STARTING_TAB_MESSAGE);
  const lines = rotating.length > 0 ? rotating : [STARTING_TAB_MESSAGE];
  const payload = JSON.stringify(lines).replace(/</g, '\\u003c');
  const first = escHtml(lines[0]);
  tab.document.write(
    `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Your Mitti</title><style>
html,body{height:100%;margin:0}
body{display:flex;align-items:center;justify-content:center;background:#f6f3ee;color:#1f1b17;font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;text-align:center;padding:24px}
.card{max-width:28rem}
.spin{width:28px;height:28px;margin:0 auto 16px;border:3px solid #e7ded1;border-top-color:#C9502E;border-radius:50%;animation:spin .8s linear infinite}
@keyframes spin{to{transform:rotate(360deg)}}
.lead{font-size:16px;font-weight:600;margin:0 0 8px}
.msg{font-size:14px;line-height:1.5;color:#6b6357;margin:0;min-height:3em}
</style></head><body><div class="card"><div class="spin" aria-hidden="true"></div><p class="lead">${escHtml(STARTING_TAB_MESSAGE)}</p><p class="msg" id="msg">${first}</p></div><script>const msgs=${payload};let i=0;const timer=setInterval(function(){const el=document.getElementById('msg');if(!el){clearInterval(timer);return;}if(!msgs.length)return;i=(i+1)%msgs.length;el.textContent=msgs[i];},${ROTATE_MS});</script></body></html>`,
  );
  tab.document.close();
}

/** Auto-submitting POST. The ticket is a form field, never a query parameter. */
export function ssoFormHtml(action: string, ticket: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Your Mitti</title><style>${WARMUP_TAB_STYLE}</style></head><body><p>${escHtml(WARMUP_MESSAGE)}</p><form method="POST" action="${escHtml(action)}"><input type="hidden" name="ticket" value="${escHtml(ticket)}"></form><script>try{window.opener=null}catch(e){}</script><script>setTimeout(function(){document.forms[0].submit()},${WARMUP_TAB_MS})</script></body></html>`;
}

type TabDoc = { open?: () => void; write: (html: string) => void; close: () => void };
export type SsoTab = { closed?: boolean; document: TabDoc; location?: { href: string } };

/** True when the blank tab was opened and has not been closed. */
export function tabUsable<T extends { closed?: boolean }>(tab: T | null | undefined): tab is T {
  return !!tab && tab.closed !== true;
}

/**
 * Navigate an already-open tab without a Referer. An anchor click with
 * rel=noreferrer is the navigation that keeps noreferrer semantics.
 */
export function fallbackNavigationHtml(href: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${WARMUP_TAB_STYLE}</style></head><body><p>${escHtml(WARMUP_MESSAGE)}</p><a id="go" rel="noreferrer noopener" href="${escHtml(href)}"></a><script>setTimeout(function(){document.getElementById('go').click()},${WARMUP_TAB_MS})</script></body></html>`;
}

export function writeFallbackNavigation(tab: SsoTab, href: string): void {
  tab.document.open?.();
  tab.document.write(fallbackNavigationHtml(href));
  tab.document.close();
}

/** Write the form, or https://<host>/ when the action is not exactly that host's /sso. */
export function deliverToTab(
  tab: SsoTab,
  host: string,
  sso: { action: string; ticket: string } | null | undefined,
): 'posted' | 'home' {
  const home = httpsOrigin(host);
  if (sso?.action && sso.ticket && ssoActionMatches(sso.action, host)) {
    try {
      tab.document.open?.();
      tab.document.write(ssoFormHtml(sso.action, sso.ticket));
      tab.document.close();
      return 'posted';
    } catch {
      /* The blank tab can refuse document access. Fall through without the ticket. */
    }
  }
  if (home) {
    try {
      writeFallbackNavigation(tab, home);
    } catch {
      // Document access refused (e.g. Safari). Navigating by location is still allowed.
      if (tab.location) tab.location.href = home;
    }
  }
  return 'home';
}

/**
 * Open a tab in the current user gesture, then mint and POST.
 * The caller's openBlank (window.open) runs before the mint await.
 */
export async function openMittiInGesture(opts: {
  openBlank: () => SsoTab | null;
  host: string;
  mint: () => Promise<{ action: string; ticket: string } | null>;
}): Promise<'posted' | 'home' | 'blocked'> {
  const tab = opts.openBlank();
  if (!tabUsable(tab)) return 'blocked';
  const sso = await opts.mint();
  if (!tabUsable(tab)) return 'blocked';
  return deliverToTab(tab, opts.host, sso);
}

export function writeSsoTab(
  tab: { document: TabDoc } | null,
  action: string,
  ticket: string,
): boolean {
  if (!tab) return false;
  try {
    tab.document.open?.();
    tab.document.write(ssoFormHtml(action, ticket));
    tab.document.close();
    return true;
  } catch {
    return false;
  }
}
